/**
 * useBoardTaskResize - mouse-based task duration resize
 * Extracted from BoardV3DraggableTaskBox.tsx for readability
 */

import { useState, useCallback, useEffect, useRef } from 'react';

type ResizeSide = 'left' | 'right' | null;

export function useBoardTaskResize(
  width: number,
  dayWidth: number,
  taskId: string,
  onResize?: (taskId: string, newDurationDays: number, newStartDayDelta?: number) => void,
) {
  const [resizeSide, setResizeSide] = useState<ResizeSide>(null);
  const [resizeOffset, setResizeOffset] = useState<{ left: number; width: number } | null>(null);
  const resizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const resizeOffsetRef = useRef<{ left: number; width: number } | null>(null);

  const isResizing = resizeSide !== null;

  const handleResizeStart = useCallback((side: ResizeSide) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    resizeStartRef.current = { startX: e.clientX, startWidth: width };
    const initialOffset = { left: 0, width: width };
    resizeOffsetRef.current = initialOffset;
    setResizeSide(side);
    setResizeOffset(initialOffset);
  }, [width]);

  useEffect(() => {
    if (!isResizing || !resizeStartRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;

      const delta = e.clientX - resizeStartRef.current.startX;
      let newOffset: { left: number; width: number };

      if (resizeSide === 'right') {
        const newWidth = Math.max(dayWidth, resizeStartRef.current.startWidth + delta);
        const snappedWidth = Math.round(newWidth / dayWidth) * dayWidth;
        newOffset = { left: 0, width: snappedWidth };
      } else {
        const snappedDelta = Math.round(delta / dayWidth) * dayWidth;
        const newWidth = Math.max(dayWidth, resizeStartRef.current.startWidth - snappedDelta);
        newOffset = { left: snappedDelta, width: newWidth };
      }

      resizeOffsetRef.current = newOffset;
      setResizeOffset(newOffset);
    };

    const handleMouseUp = () => {
      const currentOffset = resizeOffsetRef.current;

      if (resizeStartRef.current && currentOffset && onResize) {
        const newDurationDays = Math.round(currentOffset.width / dayWidth);
        const startDayDelta = Math.round(currentOffset.left / dayWidth);

        if (newDurationDays > 0) {
          onResize(taskId, newDurationDays, startDayDelta);
        }
      }

      setResizeSide(null);
      setResizeOffset(null);
      resizeStartRef.current = null;
      resizeOffsetRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeSide, dayWidth, taskId, onResize]);

  return { resizeSide, resizeOffset, isResizing, handleResizeStart };
}
